import { Injectable } from '@angular/core';

import { ReplaySubject } from 'rxjs';

import { EnableClassTelemetry } from 'app/services/otel.service';
import { Permission } from '../bridge-api/auth.service';
import { WithAuditContext } from '../http-mutating-operations.interceptor';
import { SemantriaAPIService } from '../semantria-api.service';
import { Group } from './group.service';

export interface SemantriaUser {
  user_id: string;
  login: string;
  password?: string;
  account_id: string;
  email: string;
  alternate_email: string;
  first_name: string;
  last_name: string;
  company_name: string;
  password_modified?: Date;
  last_login?: Date;
  creation_date?: Date;
  modification_date?: Date;
  user_groups: string[];
  permissions: Permission[];
}

export interface LegacySemantriaUser {
  user_id: string;
  login: string;
  password?: string;
  account_id: string;
  email: string;
  alternate_email: string;
  first_name: string;
  last_name: string;
  company_name: string;
  password_modified?: Date;
  last_login?: Date;
  creation_date?: Date;
  modification_date?: Date;
  user_groups: UserInputGroup[];
  permissions: Permission[];
}

export interface UserInputGroup {
  group_id: string;
}

export interface UserPageResult<T> {
  data: T[];
  page: number;
  page_size: number;
  total_elements: number;
  total_pages: number;
}

@EnableClassTelemetry()
@Injectable()
export class SemantriaUsersService extends SemantriaAPIService {
  userGroupChange$ = new ReplaySubject<Group>();

  legacyUserFromUser(user: SemantriaUser): LegacySemantriaUser {
    const legacyUser: LegacySemantriaUser = {
      user_id: user.user_id,
      login: user.login,
      password: user.password,
      account_id: user.account_id,
      email: user.email,
      alternate_email: user.alternate_email,
      first_name: user.first_name,
      last_name: user.last_name,
      company_name: user.company_name,
      password_modified: user.password_modified,
      last_login: user.last_login,
      creation_date: user.creation_date,
      modification_date: user.modification_date,
      permissions: user.permissions,
      user_groups: [],
    };
    for (const group of user.user_groups) {
      if (group) {
        legacyUser.user_groups.push({ group_id: group });
      }
    }
    return legacyUser;
  }

  static isAccountAdmin(user: SemantriaUser) {
    return user.permissions?.some((p) => p.permission_type === 'ACCOUNT_ADMIN') ?? false;
  }

  static getAccountAdminPermission(user: SemantriaUser): Permission {
    return user.permissions?.find((p) => p.permission_type === 'ACCOUNT_ADMIN');
  }

  static isExecutive(user: SemantriaUser, groupId: string) {
    return (
      user.permissions?.some(
        (p) => p.permission_type === 'EXECUTIVE_READ_ONLY' && p.group_id === groupId
      ) ?? false
    );
  }
  static getGroupPermissions(user: SemantriaUser, groupId: string): Permission[] {
    return user.permissions?.filter((p) => p.group_id === groupId) ?? [];
  }

  static getReadOnlyGroupPermission(user: SemantriaUser, groupId: string): Permission {
    return user.permissions?.find(
      (p) => p.permission_type === 'READ_ONLY' && p.group_id === groupId
    );
  }

  static getAllReadOnlyPermissions(user: SemantriaUser): Permission[] {
    return (
      user.permissions?.filter(
        (p) =>
          p.permission_type === 'READ_ONLY' ||
          p.permission_type === 'READ_ONLY_SSV' ||
          p.permission_type === 'EXECUTIVE_READ_ONLY'
      ) ?? []
    );
  }

  getUsers(): Promise<SemantriaUser[]> {
    return this.get('/users/');
  }

  getAdmins(page = 0, pageSize = 100, search?: string): Promise<UserPageResult<SemantriaUser>> {
    const params = {
      page_number: page,
      page_size: pageSize,
      admins_only: true,
      sort: 'email',
      exclude_id: true,
    };
    if (search) {
      params['search_string'] = search;
    }

    return this.get(`/users/search/`, params);
  }

  searchUsers(
    page = 0,
    pageSize = 100,
    search?: string,
    groupId?: string
  ): Promise<UserPageResult<SemantriaUser>> {
    const params = {
      page_number: page,
      page_size: pageSize,
      sort: 'email',
      group_id: groupId,
      exclude_id: true,
    };
    if (search) {
      params['search_string'] = search;
    }

    return this.get(`/users/search/`, params);
  }

  getUser(userId: string): Promise<SemantriaUser> {
    return this.get(`/users/${userId}`);
  }

  @WithAuditContext('user-created', (_, args) => {
    return {
      email: args[0].email,
      permissions: ((args[0].permissions ?? []) as Permission[]).map((p) => p.permission_type),
    };
  })
  createUser(user: SemantriaUser): Promise<SemantriaUser> {
    const legacyUser = this.legacyUserFromUser(user);
    return this.post(`/users/`, { ...legacyUser });
  }

  @WithAuditContext('user-updated', (_, args) => {
    return {
      email: args[0].email,
      permissions: ((args[0].permissions ?? []) as Permission[]).map((p) => p.permission_type),
    };
  })
  updateUser(user: SemantriaUser): Promise<SemantriaUser> {
    const legacyUser = this.legacyUserFromUser(user);
    return this.put(`/users/${user.user_id}/`, { ...legacyUser });
  }

  @WithAuditContext('user-permission-added', (_, args) => {
    return { id: args[0], permissionType: args[1], groupId: args[2] };
  })
  addPermission(id, permissionType, groupId?): Promise<Permission> {
    const permission = {
      user_id: id,
      permission_type: permissionType,
    } as any;

    if (groupId) {
      permission.group_id = groupId;
    }

    return this.post(`/users/${id}/permissions/`, permission);
  }

  @WithAuditContext('user-permission-added', (_, args) => {
    return { id: args[0], permissionType: 'READ_ONLY', groupId: args[2] };
  })
  async addReadOnlyGroupPermission(id, groupId): Promise<Permission> {
    return await this.post<Promise<Permission>>(`/users/${id}/permissions/`, {
      user_id: id,
      permission_type: 'READ_ONLY',
      group_id: groupId,
    });
  }

  @WithAuditContext('user-permission-deleted', (_, args) => {
    return { id: args[0], permissionId: args[1] };
  })
  removePermission(userId, permissionId): Promise<Permission> {
    return this.delete(`/users/${userId}/permissions/${permissionId}`);
  }

  resetPassword(): Promise<void> {
    return Promise.resolve();
  }

  getDisplayNameForUser(userId: string): Promise<string> {
    return this.getUser(userId).then(
      (user) => ((user.first_name || '') + ' ' + (user.last_name || '')).trim() || user.email
    );
  }
}
